import { DocumentArrowUpIcon, UserGroupIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { IMPORT_SOURCES } from '../../../@types/ImportSource';
import type { RootState } from '../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';
import CompanySearch from '../components/CompanySearch';

interface Company {
  id: number;
  name: string;
  siren: string;
}

interface SciImportListItem {
  id: number;
  createdAt: string;
  isTestImport: boolean;
  data: { summary?: Record<string, number> } | null;
}

const SciContactImport: React.FC = () => {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const navigate = useNavigate();

  const [company, setCompany] = useState<Company | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [imports, setImports] = useState<SciImportListItem[]>([]);

  const fetchImports = useCallback(async(companyId: number) => {
    try {
      const response = await fetch(`/api/imports?companyId=${companyId}&importSource=${IMPORT_SOURCES.SCI_EXCEL}`, JWTHeader(jwt));
      if (response.ok) {
        setImports(await response.json());
      }
    } catch (error) {
      console.error('Error fetching SCI imports:', error);
    }
  }, [jwt]);

  useEffect(() => {
    setImports([]);
    if (company) {
      fetchImports(company.id);
    }
  }, [company, fetchImports]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    if (selected && !/\.xlsx?$/i.test(selected.name)) {
      showToast('error', 'Format non supporté : utilisez le fichier Excel (.xlsx) exporté depuis la liste des SCI.');
      return;
    }
    setFile(selected);
  };

  const handleAnalyze = async() => {
    if (!company || !file || !jwt) {return;}

    setAnalyzing(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`/api/companies/${company.id}/imports/${IMPORT_SOURCES.SCI_EXCEL}/compare`, {
        method: 'POST',
        headers: { Authorization: JWTAuthorization(jwt) },
        body: formData,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || 'Analyse impossible.');
      }

      showToast('success', 'Analyse terminée : vérifiez les rapprochements avant d\'écrire les contacts.');
      navigate(ROUTES.ADMIN_IMPORT_DETAIL.replace(':importId', String(result.importId)));
    } catch (error) {
      console.error('SCI import analysis error:', error);
      showToast('error', error instanceof Error ? error.message : 'Erreur lors de l\'analyse du fichier.');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="mx-auto">
      <div className="mb-8 rounded-lg bg-white p-6 shadow-md">
        <h1 className="mb-4 flex items-center text-2xl font-bold text-gray-800">
          <UserGroupIcon className="mr-2 h-8 w-8 text-rgpd-blue-dark" />
          Import des contacts SCI
        </h1>
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
          <p>
            Importez l&apos;export Excel de la liste des SCI après que le client a complété les colonnes contact
            (civilité, nom, prénom, téléphone, email, adresse).
          </p>
          <p className="mt-2">
            Un contact n&apos;est retenu que s&apos;il correspond à un dirigeant de la SCI (par le nom ou par l&apos;email).
            Rien n&apos;est écrit avant la revue des rapprochements.
          </p>
          <p className="mt-2">
            La colonne « Client en gestion » (Oui / Non) est aussi lue : elle est écrite à la validation pour chaque SCI
            du périmètre, même sans contact. Une cellule vide ne change rien.
          </p>
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-gray-700">Entreprise</label>
          <CompanySearch onCompanySelect={setCompany} showClearButton />
        </div>

        <div className="mb-6">
          <label htmlFor="dropzone-file-sci-excel" className="mb-2 block text-sm font-medium text-gray-700">Fichier SCI complété</label>
          <label htmlFor="dropzone-file-sci-excel" className="flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100">
            <DocumentArrowUpIcon className="mb-3 h-10 w-10 text-gray-400" />
            <p className="text-sm text-gray-500">
              <span className="font-semibold">Cliquez pour choisir le fichier</span>
              {' '}
              (XLS, XLSX)
            </p>
            {file && <p className="mt-2 text-sm font-medium text-gray-700">{file.name}</p>}
            <input id="dropzone-file-sci-excel" type="file" className="hidden" accept=".xls,.xlsx" onChange={handleFileChange} />
          </label>
        </div>

        <button
          type="button"
          onClick={handleAnalyze}
          disabled={!company || !file || analyzing}
          className="flex items-center gap-2 rounded-md bg-rgpd-blue-dark px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {analyzing && <Spinner size="sm" />}
          {analyzing ? 'Analyse en cours…' : 'Analyser le fichier'}
        </button>
      </div>

      {company && imports.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow-md">
          <h2 className="mb-4 text-lg font-semibold text-gray-800">
            Imports de contacts SCI —
            {' '}
            {company.name}
          </h2>
          <ul className="divide-y divide-gray-100 text-sm">
            {imports.map((item) => (
              <li key={item.id} className="flex items-center justify-between py-2">
                <Link className="text-rgpd-blue-dark hover:underline" to={ROUTES.ADMIN_IMPORT_DETAIL.replace(':importId', String(item.id))}>
                  {`#${item.id} — ${new Date(item.createdAt).toLocaleString('fr-FR')}`}
                </Link>
                <Badge size="small" mode={item.isTestImport ? 'warning' : 'success'} text={item.isTestImport ? 'Analyse (revue)' : 'Contacts écrits'} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SciContactImport;
